import type { MetaSchemaField } from "../../../components/MetaSchemaEditor/MetaSchemaEditor";
import type { SchemaField } from "../../../components/SchemaEditor/SchemaEditor";

/**
 * ProjectConfig — shape of `games.config` JSONB blob the admin edits.
 *
 * Mockup: project-config.html L447-L746. Backend stores this as opaque JSON;
 * the UI is the source of truth for the sub-keys and their defaults
 * (see ./defaults.ts).
 */
export interface ProjectConfig {
  chat: ChatConfig;
  guild: GuildConfig;
  help_request: HelpRequestConfig;
  gift: GiftConfig;
  invites: InvitesConfig;
  rate_limits: RateLimitsConfig;
  auth: AuthConfig;
  profile: ProfileConfig;
  cloud_save: CloudSaveConfig;
  guild_assets: GuildAssetsConfig;
  request_signing: RequestSigningConfig;
}

/**
 * One save per player in the platform's bucket. Two keys, and the second is
 * bytes in the blob while the form shows MiB — the conversion lives in the
 * page, and the helper under the field prints the exact byte count so Raw
 * JSON and the form never disagree in an operator's head.
 */
export interface CloudSaveConfig {
  /** Off by default: a project that never asked has no save flow. */
  enabled: boolean;
  /**
   * Ceiling on one save as stored — compressed, when the SDK compresses. The
   * server clamps to 1–64 MiB; the form refuses anything outside it.
   */
  max_bytes: number;
}

/**
 * No rate limit here. How many messages a minute a player may send is
 * `rate_limits.chat_per_player_per_minute` — the key both the REST chat group
 * and the websocket read — and it is edited in the Rate limits section with the
 * other five. `chat.rate_limit_per_minute` used to sit in this section, was
 * saved on every press of the button, and was read by nothing; migration 0075
 * moved what operators typed into it to the key that is read.
 */
export interface ChatConfig {
  history_window: number;
  max_message_length: number;
  /** Custom profanity words. Backend merges with its own default list. */
  profanity_words: string[];
}

export interface GuildConfig {
  max_members: number;
  require_unique_name: boolean;
  metadata_schema: SchemaField[];
  recommendations: GuildRecommendationsConfig;
  /**
   * The `int` field of `profile.stats_schema` that ranks members by strength
   * when a guild's leader's seat falls vacant — the leader leaves, or their
   * account is deleted. An officer still inherits before any plain member;
   * this decides who within each group, ahead of length of service.
   *
   * A flat key, not a pair like `recommendations`: succession asks one
   * question ("who is stronger"), and one key answers it. It is also its own
   * key rather than a reuse of `requirement_player_key` — "what it takes to
   * get in" and "who leads next" are two decisions, and a project that
   * switches its recommendation feed off must not lose its succession order.
   *
   * An empty string means "not set": succession then goes officer first, then
   * longest-serving, which is what it did before this key existed.
   */
  succession_level_key: string;
}

/**
 * Numeric gate for `GET /v1/guilds/recommended`: a guild whose
 * `guilds.metadata[requirement_guild_key]` exceeds the player's own
 * `players.stats[requirement_player_key]` is hidden, and the closer the two
 * are, the higher the guild ranks.
 *
 * Both keys point at `int` fields of the schemas already declared on this
 * project (`guild.metadata_schema` / `profile.stats_schema`). An empty string
 * means "not set" — the criterion only applies when BOTH sides are filled in.
 */
export interface GuildRecommendationsConfig {
  requirement_guild_key: string;
  requirement_player_key: string;
}

/**
 * How the player takes what was donated to a request of this type.
 *
 * `all_at_once` is what the platform did before the mode existed, so it is
 * both the default and what a bare string in `types` reads as — see
 * `normalizeHelpTypes`.
 */
export type HelpClaimMode = "all_at_once" | "one_at_a_time";

/** One entry of `help_request.types`: a `request_type` the API accepts, the
 *  mode by which its donations are taken, and how often it may be asked for. */
export interface HelpRequestType {
  type: string;
  claim: HelpClaimMode;
  /**
   * At most this many requests of this type per `window_seconds`, over a
   * window sliding back from now. "Once every 4 hours" is 1 per 14400.
   *
   * Both quota keys are OPTIONAL and only mean anything as a pair: a type
   * carrying neither is unlimited, which is what every type is today and what
   * a newly added one starts as. Absent stays absent — the panel never writes
   * the keys back until a designer fills both boxes, so upgrading the platform
   * neither limits anybody nor shows a diff on a config nobody edited (see
   * `normalizeHelpTypes`). Half a pair is a row error that blocks Save, since
   * the server would ignore it while the designer thinks it is set.
   */
  max_per_window?: number;
  /** Length of the window `max_per_window` is counted over, in seconds. */
  window_seconds?: number;
  /**
   * How much ONE request of this type may gather in total. It is fulfilled the
   * moment donations add up to it; absent means no cap, and the request then
   * stays open until it expires.
   *
   * Optional and absent-when-unset, like the quota pair above — but a single
   * key, so there is no half-filled state to block Save on.
   */
  max_amount?: number;
  /**
   * How long a request of this type stays open, in seconds. Absent means the
   * section's `default_ttl_seconds` applies — which, unlike the keys above, is
   * a value the server really does fall back to, so a type without this key is
   * configured, not unlimited.
   *
   * `0` is the third reading, and the only one that is not a duration: requests
   * of this type NEVER expire and close only when they are fulfilled or
   * cancelled. It is what the row's ∞ toggle writes, and what the server reads
   * the same way it reads `unclaimed_ttl_seconds: 0`.
   */
  ttl_seconds?: number;
  /**
   * What a player gets back for donating to a request of this type. Arbitrary
   * JSON the platform never reads: an object (`{"coins": 50}`) or a list of
   * items (`[{"currency": "Coins", "amount": 600}]`), and nothing else — see
   * lib/reward.ts for why a bare scalar is not a reward. Absent means the type
   * rewards nobody, which is every type of every project that predates the key.
   *
   * `unknown` rather than a shape, because a shape is exactly what this does
   * not have. While the operator is still typing one, the field holds THEIR
   * TEXT as a string instead of a value — an unsaveable state the row paints
   * red and `helpTypesError` blocks Save on, and the only way a string ever
   * appears here (a string is not a reward).
   */
  donor_reward?: unknown;
  /**
   * What a request of this type has to SAY about itself: the fields its
   * `payload` may and must carry, in the same `{key, type, allowed}` form that
   * describes a player's card, plus `required`.
   *
   * ABSENT IS NOT EMPTY, and the difference is the whole compatibility story.
   * A type with no schema accepts any payload including none at all, which is
   * what every type of every project did before this key existed; declaring the
   * first field is what starts refusing anything else. So the key stays absent
   * until a designer adds a field, exactly like the quota pair above — see
   * `normalizeHelpTypes`.
   */
  payload_schema?: MetaSchemaField[];
}

/**
 * The `help_request` section. All three of its durations carry the same extra
 * reading: `0` is not a length of time but "never expires", which is what the ∞
 * toggle beside each of them writes.
 */
export interface HelpRequestConfig {
  types: HelpRequestType[];
  /**
   * How long a donation stays takeable, counted from the donation rather than
   * from the request. What is left when it runs out burns — unless this is `0`,
   * and then nothing burns.
   */
  unclaimed_ttl_seconds: number;
  /** How long a request stays open when its type sets no `ttl_seconds`; `0`
   *  makes every such type's requests eternal at once. */
  default_ttl_seconds: number;
}

/**
 * Who a player is allowed to send a gift to. An allow-list, checked when the
 * gift is SENT and never again: leaving the guild afterwards does not take back
 * what already arrived. A send outside every enabled channel is refused with
 * `gift_recipient_not_eligible`, so an empty list means gifts go nowhere.
 */
export type GiftChannel = "friends" | "guild" | "code";

/**
 * How the recipient takes a gift out of their inbox — the same two modes a help
 * donation is taken by, and deliberately the same TYPE rather than a second
 * copy of it: the inbox reads one rule off the live config at claim time, and
 * two spellings of "all at once" would be two rules to keep in step.
 */
export type GiftClaimMode = HelpClaimMode;

/**
 * One entry of `gift.types`: a `type` the API accepts, the mode by which it is
 * taken, and the quotas that are the whole brake on sending it.
 *
 * Only `type` is required. Everything below it is optional and absent-when-
 * unset, by the same rule `HelpRequestType` states at length: a key the panel
 * never wrote must not appear, or upgrading the platform shows a diff on a
 * config nobody edited.
 *
 * What a gift row does NOT carry is as deliberate as what it does. There is no
 * `ttl_seconds` — a gift has no life of its own to run out, only the section's
 * `unclaimed_ttl_seconds` — and no `donor_reward`: nothing is debited from the
 * sender, so there is nothing to pay them back for.
 */
export interface GiftType {
  type: string;
  claim: GiftClaimMode;
  /**
   * At most this many gifts of this type per `window_seconds`, counted over a
   * window sliding back from now, to EVERYBODY together. Every send counts,
   * whether or not it was ever taken.
   *
   * Meaningful only as a pair with `window_seconds`: a type carrying neither is
   * unlimited, and half a pair is a row error that blocks Save, since the
   * server would ignore it while the designer believes the type is limited.
   */
  max_per_window?: number;
  /** Length of the window `max_per_window` is counted over, in seconds. */
  window_seconds?: number;
  /** How much may ride on ONE send. Absent means no cap. A single key, so —
   *  unlike the pairs — it has no half-filled state to block Save on. */
  max_amount?: number;
  /**
   * The same quota narrowed to one pair of players: how often the sender may
   * give this type to the SAME recipient. This is the rule that stops one
   * player being buried by one other, which the quota above cannot express —
   * "20 lives a day" is generous spread over a guild and abuse aimed at one
   * person.
   *
   * Its own pair, obeying its own halves: a type may limit the total, the pair,
   * both or neither.
   */
  pair_max_per_window?: number;
  /** Length of the window `pair_max_per_window` is counted over, in seconds. */
  pair_window_seconds?: number;
}

/**
 * The `gift` section — a player sending something straight to another player,
 * with no request having asked for it.
 *
 * Both lists are allow-lists, and an empty one is a refusal rather than a
 * default: no `types` means gifts are off for this project entirely, and no
 * `channels` means there is nobody left to send to. There is no separate
 * "gifts enabled" switch, because it would be a second place obliged to agree
 * with the lists.
 */
export interface GiftConfig {
  channels: GiftChannel[];
  types: GiftType[];
  /**
   * How long a gift stays takeable after it arrives, counted from the send.
   * What is left when it runs out burns — unless this is `0`, which is the
   * "never expires" the ∞ toggle writes and the server reads, exactly as it
   * does for the Help section's durations.
   */
  unclaimed_ttl_seconds: number;
}

/**
 * The `invites` section — a player shares a friend code, and whoever installs
 * the game through it pays them both back once the invitee reaches a trigger.
 *
 * The trigger is a PAIR and only counts as set when BOTH halves are
 * (`trigger_stat_key !== "" && trigger_value > 0`), which is the server's own
 * rule. Half a trigger is not a stricter trigger but no trigger at all: both
 * sides are then paid the moment the code is accepted, and the form has to say
 * so rather than let an operator read the half they filled in.
 */
export interface InvitesConfig {
  /** Off by default: a project that never asked has no referral flow. */
  enabled: boolean;
  /**
   * The `int` field of `profile.stats_schema` the trigger watches, in the raw
   * units `players.stats` holds — not the number on the player's screen.
   */
  trigger_stat_key: string;
  /** What that field has to reach. `0` is "no threshold", see above. */
  trigger_value: number;
  /**
   * What the invited player gets. Arbitrary JSON no Go path reads the keys of —
   * an object or a list of items, by the one rule in lib/reward.ts.
   *
   * `unknown` rather than a shape, because a shape is what this does not have.
   * While the operator is still typing one the key holds THEIR TEXT as a
   * string — an unsaveable state the row paints red and the save bar blocks
   * on, and the only way a string ever appears here (a string is not a
   * reward). Absent means the invited player is paid nothing and gets no
   * inbox item.
   */
  invitee_reward?: unknown;
  /** Rides on the invited player's inbox item as its `source_type`. */
  invitee_reward_name: string;
  /** What the inviter gets for their Nth friend to reach the trigger. Empty —
   *  the inviter is paid nothing, and nobody's friends are counted. */
  inviter_rewards: InviterRewardStep[];
  /**
   * How long a granted reward waits in the inbox before what is left burns,
   * counted from the grant. `0` is the "never expires" the ∞ toggle writes and
   * the server reads, exactly as the gift and help durations' is.
   */
  reward_ttl_seconds: number;
}

/**
 * One step of the inviter's ladder: friends `from`…`to`, counted in the order
 * they reached the trigger, each pay `reward` under `name`.
 *
 * `to: null` is "no end" — the ∞ toggle's value, and the server's. An emptied
 * box is `undefined`, as in every other grid on the page, and on the wire an
 * absent key — which the server reads as a broken step, never as no end. So
 * `null` is a meaning here and `undefined` a blank, and the two never meet.
 *
 * All four keys are always present, in this order, even when one is
 * `undefined`: the save bar diffs through JSON, and a key re-added at the end
 * would read as a change nobody made.
 */
export interface InviterRewardStep {
  from: number | undefined;
  to: number | null | undefined;
  /** The inviter's inbox item's `source_type`. */
  name: string;
  /** The same `unknown` as `invitee_reward`, for the same reason; `undefined`
   *  is a step with no reward yet, which no saved ladder may hold. */
  reward: unknown;
}

/**
 * Every rate limit the platform enforces, and nothing else.
 *
 * All eight keys, in one section, from the widest to the narrowest. Three of them
 * were enforced for a long time with no box to set them in — a limit an
 * operator cannot see is one they discover from a support ticket — and the chat
 * one had a box in the Chat section that saved to a key nothing read.
 *
 * The GAME quotas are deliberately not here: `max_per_window` on a help type,
 * `limit`/`pair_limit` on a gift type, `max_friends`. Those are attributes of a
 * row in a list of types, they live with the type, and hoisting them would
 * create a second place obliged to know every type this project has.
 */
export interface RateLimitsConfig {
  /**
   * Every authenticated request in the project, counted per game rather than
   * per player: the ceiling on what one project can ask of the platform.
   */
  global_per_game_per_minute: number;
  auth_per_ip_per_minute: number;
  /** Chat messages per player — the REST group and the WS frame share it. */
  chat_per_player_per_minute: number;
  /** Only `POST /v1/guilds/{id}/help-requests`; reads and donations are free. */
  help_create_per_minute: number;
  /** Sending a friend request, and importing friends from Facebook. */
  friend_requests_per_minute: number;
  /**
   * How many gifts one player may send in a minute. A machine brake against
   * loops and scripts, not a game rule — the game rule is the quota on the
   * type, which counts over hours and days rather than over a minute.
   */
  gifts_per_minute: number;
  /**
   * Cloud-save writes per player: upload-url requests, finalize calls and
   * deletes. Each accepted upload-url is a fresh object the client may fill,
   * so this is what bounds how fast one player can make the bucket grow.
   * Status checks and download URLs ride on the global per-game cap.
   */
  cloud_save_per_minute: number;
  /**
   * Attempts to ACCEPT a friend code, per player. The same bucket family as
   * `friend_requests_per_minute` — a brake on a script rather than a game rule,
   * the game rule being `invites.inviter_rewards`.
   */
  invites_per_minute: number;
}

export interface AuthConfig {
  facebook: FacebookAuthConfig;
}

/**
 * Facebook app credentials. Used server-side only — to verify tokens issued
 * by the provider SDK and to fetch profile avatars. `app_secret` is stored
 * plaintext in `games.config`, same policy as the project API key.
 */
export interface FacebookAuthConfig {
  enabled: boolean;
  app_id: string;
  app_secret: string;
}

export interface ProfileConfig {
  stats_schema: SchemaField[];
  /**
   * Cosmetic fields in `players.meta`. Unlike `stats_schema`, an EMPTY
   * meta_schema is a strict refusal: the backend rejects every key.
   */
  meta_schema: MetaSchemaField[];
  allowed_avatars: string[];
}

export interface GuildAssetsConfig {
  allowed_logos: string[];
}

/** The three modes the server accepts. Anything else reads as `off`. */
export type SigningMode = "off" | "log" | "enforce";

/**
 * `request_signing` — how much the platform does about the signature every
 * authenticated request carries.
 *
 * Both fields are typed wider than the values the form can produce, and that is
 * the section's whole point rather than sloppiness: an operator reaches these
 * keys through Raw JSON and through the platform API too, and what they wrote
 * there is what this page has to show them. Narrowing `mode` to SigningMode
 * would mean normalizeConfig quietly rewriting a typo to `off` — a change the
 * operator never made, saved back over the word they meant to fix.
 *
 * What each wrong value actually DOES is not decided here: the server's parser
 * decides, and says so through the flags on GET /signing-stats.
 */
export interface RequestSigningConfig {
  mode: SigningMode | string;
  /**
   * Left `unknown` rather than narrowed, and the width is the point: `true`,
   * `{}` and a number too large for an int64 are all values the server reads as
   * "not a whole number", and coercing them to 300 here would put the default
   * in the box beside a helper line saying the default is fine — hiding the
   * mistake from the one person who made it.
   */
  clock_skew_sec: unknown;
}

/**
 * Section keys — used for per-section dirty tracking, mod-dot rendering,
 * and the human title / sub-label rendered in each section header.
 *
 * `bots` is absent from this list and from ProjectConfig above, and the two
 * absences now say the same thing: the simulation is not part of this blob. It
 * is its own document in its own table, edited on the Bots page — see
 * features/bots/config/types.ts.
 */
export type SectionKey =
  | "chat"
  | "guild"
  | "help_request"
  | "gift"
  | "invites"
  | "rate_limits"
  | "auth"
  | "profile"
  | "cloud_save"
  | "guild_assets"
  | "request_signing";

export const SECTION_ORDER: SectionKey[] = [
  "chat",
  "guild",
  "help_request",
  // Right after Help requests, which is the mechanic it is the other half of:
  // «ask the guild» and «send it straight» are one feature to an operator, and
  // they are tuned together.
  "gift",
  // After Gifts and before the rate limits: it is a game mechanic an operator
  // tunes with the other two, not a piece of infrastructure.
  "invites",
  "rate_limits",
  "auth",
  "profile",
  // After Profile — it is player data too — and before the guild assets.
  "cloud_save",
  "guild_assets",
  // Last, after the guild assets. This section is about the transport rather
  // than about the game, and it has nothing to wedge between the profile and a
  // guild's logos.
  "request_signing",
];

export const SECTION_META: Record<
  SectionKey,
  { title: string; apiName: string }
> = {
  chat: { title: "Chat & profanity", apiName: "games.config.chat" },
  guild: { title: "Guild defaults", apiName: "games.config.guild" },
  help_request: {
    title: "Help requests",
    apiName: "games.config.help_request",
  },
  gift: { title: "Gifts", apiName: "games.config.gift" },
  invites: { title: "Invites", apiName: "games.config.invites" },
  rate_limits: { title: "Rate limits", apiName: "games.config.rate_limits" },
  auth: { title: "Authentication", apiName: "games.config.auth" },
  profile: { title: "Profile", apiName: "games.config.profile" },
  cloud_save: { title: "Cloud saves", apiName: "games.config.cloud_save" },
  guild_assets: {
    title: "Guild assets",
    apiName: "games.config.guild_assets",
  },
  request_signing: {
    title: "Request signing",
    apiName: "games.config.request_signing",
  },
};
